<template>
  <div data-testid="success-registration-page">
    <v-container class="container">
      <v-row align="center" justify="center" wrap style="margin: 3rem 0 5rem 0" v-if="!loggedIn">
        <v-col cols="12" sm="10" md="8" style="margin-top: 48px">
          <!-- Success Animation/Confetti -->
          <div class="success-animation" data-testid="success-animation">
            <img src="@/assets/Objects (1).svg" alt="Celebration" class="confetti-svg" />
          </div>

          <!-- Success Message -->
          <div class="success-content" data-testid="success-content">
            <h1 class="success-title" data-testid="success-title">Account created successfully!</h1>
            <p class="success-subtitle" data-testid="success-subtitle">
              Thank you for registering with us. Your information has been received.
            </p>

            <!-- Next Steps -->
            <div class="next-steps">
              <h2 class="next-steps-title">Here's what's next:</h2>

              <div class="step-item">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h3 class="step-title">
                    Login with your email <a @click="handleLogin" class="login-link" data-testid="login-link">here</a>
                  </h3>
                </div>
              </div>

              <div class="step-item">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h3 class="step-title">Access your Learner Dashboard</h3>
                </div>
              </div>

              <div class="step-item">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h3 class="step-title">Enroll in a course</h3>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row align="center" justify="center" wrap style="margin: 3rem 0 5rem 0" v-else>
        <div class="orientationFont" style="margin: 1rem 0; height: 14rem">
          <v-row align="center" justify="center" wrap>
            <v-col cols="10" sm="10" md="10" lg="11">
              <div style="text-align: center">{{ successMessage }}</div>
            </v-col>
            <div style="text-align: center">
              <!-- <v-btn @click="next" round style="margin-top:10px" v-if="isLoggedIn">
          Dashboard
          <v-icon v-if="loggedIn">mdi-arrow-right</v-icon>
        </v-btn>-->
              <div class="login-button" v-if="loggedIn">
                <Button @click="next" variant="gold" size="large" style="margin-top: 20px; width: 12rem">
                  Dashboard
                  <template #endIcon>
                    <v-icon style="padding-left: 8px">mdi-arrow-right</v-icon>
                  </template>
                </Button>
              </div>
              <div class="login-button" v-else>
                <Button @click="handleLogin()" variant="gold" style="margin-top: 20px">Log In</Button>
              </div>
            </div>
          </v-row>
        </div>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useUserStore } from '@/stores/user';
import { useAuthStore } from '@/stores/auth';
import Button from '@/components/ui/Button.vue';

import { FetchPartnerData } from '../mixins/fetchPartnerDataMixin.js';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
import { AjaxLoader } from '../mixins/ajaxLoader.js';
import { Logger } from '../mixins/logger.js';
import { isFeatureEnabled } from '../../feature-flags';
import { getAttributionParams } from '@/util/param-persistence';

export default defineComponent({
  name: 'SuccessRegistration',

  components: {
    Button
  },

  data() {
    return {
      contact: {},
      email: sessionStorage.getItem('registrationEmail') || this.$route.params.email || ''
    };
  },

  mixins: [FetchPartnerData, DataLayerMixin, AjaxLoader, Logger],

  computed: {
    welcomePageTitle() {
      const userStore = useUserStore();
      return userStore.welcomePageData.studentPortalTitle;
    },

    shouldLoginWithAsurite() {
      const authStore = useAuthStore();
      return (
        authStore.performSignup || !!this.$conn.learnerData?._asuRite || this.$conn.getPartnerName() === 'starbucks'
      );
    },

    loggedIn() {
      return this.$auth.isAuthenticated();
    },

    successMessage() {
      return 'Please visit your dashboard to begin enrolling in courses.';
    }
  },

  methods: {
    handleLogin() {
      // Use custom OAuth for the login link since this is for non-ASU users
      if (this.shouldLoginWithAsurite) {
        this.logInfo('calling asurite login from success page');

        this.$auth.login('asurite');
      } else {
        this.logInfo('calling custom oauth login from success page with email:', this.email);

        this.$auth.login('custom_oauth', { email: this.email });
      }

      // Clear the email from sessionStorage after using it
      sessionStorage.removeItem('registrationEmail');
    },

    next() {
      if (this.loggedIn) {
        const authStore = useAuthStore();
        authStore.setPerformSignup(false);

        // Check if there's a pending certificate registration (if feature enabled)
        const pendingCertificate = sessionStorage.getItem('pendingCertificate');
        if (pendingCertificate && isFeatureEnabled('CERTIFICATE_MANAGEMENT')) {
          // Pass certificate as query param when navigating to dashboard
          this.$router.push({
            name: 'dashboard',
            query: { certificate: pendingCertificate }
          });
        } else {
          this.$router.push({ name: 'dashboard' });
        }
      } else {
        this.$auth.login('asurite');
      }
    }
  },

  created() {
    const userStore = useUserStore();
    if (userStore.welcomePageData.welcomePageTitle == undefined) {
      this.fetchPartnerData();
    }

    // Capture certificate parameter if present in URL (fallback, if feature enabled)
    if (
      this.$route.query.certificate &&
      !sessionStorage.getItem('pendingCertificate') &&
      isFeatureEnabled('CERTIFICATE_MANAGEMENT')
    ) {
      sessionStorage.setItem('pendingCertificate', this.$route.query.certificate);
    }

    window.scrollTo(0, 0);
  },

  mounted() {
    const { campaign, partner } = getAttributionParams();

    // Fire registration success event for GTM/Meta tracking
    // Send the registration submitted event from confirmRegisteration config
    this.asuoDataLayerClick('confirmRegisteration', '1', { campaign, partner });

    // Also send a custom sign_up event for Meta and GA4
    const signUpEvent = {
      event: 'sign_up',
      method: 'ulc_registration',
      campaign,
      partner,
      page_location: window.location.href,
      page_path: this.$route.path
    };

    if (window.dataLayer) {
      window.dataLayer.push(signUpEvent);
    }

    // Send page view event
    const pageViewEvent = {
      event: 'page_view',
      page_type: 'registration_success',
      page_title: 'Account created successfully',
      page_location: window.location.href,
      page_path: this.$route.path
    };

    if (window.dataLayer) {
      window.dataLayer.push(pageViewEvent);
    }
  }
});
</script>

<style lang="stylus" scoped>
.welcome-desktop2-header {
  font-size: 48px;
  font-weight: 900;
  padding: 0 7.79px;
  background-color: #FFC627;
  text-align: center;
}

.orientationFont {
  margin: 15px;
  display: flex;
  justify-content: space-between;
}

.success-animation {
  text-align: center;
  margin-bottom: 32px;
}

.confetti-svg {
  max-width: 600px;
  height: auto;
}

.success-content {
  text-align: left;
  max-width: 600px;
  margin: 0 auto;
}

.success-title {
  font-size: 36px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.success-subtitle {
  font-size: 18px;
  color: #666;
  margin-bottom: 48px;
  line-height: 1.5;
}

.next-steps {
  text-align: center;
  margin-bottom: 48px;
  position: relative;
}

.next-steps-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 32px;
  text-align: left;
}

.step-item {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-bottom: 32px;
  padding: 0;
  background: transparent;
  border-radius: 0;
  position: relative;
  // max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 15px;
  top: 32px;
  width: 2px;
  height: 32px;
  background: #ddd;
  z-index: 1;
}

.step-number {
  background: #FFC627;
  color: #333;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 16px;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.step-content {
  flex: 1;
  text-align: left;
}

.step-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0;
  line-height: 1.4;
}

.login-link {
  color: #8C1D40;
  text-decoration: underline;
  cursor: pointer;
}

.login-link:hover {
  text-decoration: none;
}

.profile-icon {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.profile-initials {
  color: white;
  font-size: 32px;
  font-weight: bold;
}

@media (max-width: 600px) {
  .welcome-desktop2-header {
    font-size: 30px;
  }

  .success-title {
    font-size: 28px;
  }

  .success-subtitle {
    font-size: 16px;
  }

  .next-steps-title {
    font-size: 20px;
  }

  .step-item {
    flex-direction: row;
    text-align: left;
  }

  .step-item:not(:last-child)::after {
    left: 15px;
  }

  .step-number {
    margin-right: 16px;
    margin-bottom: 0;
  }

  .step-title {
    font-size: 16px;
  }
}
</style>
